import { useEffect, useRef, useState } from "react";
import { ActivityIndicator, Animated, Easing, Platform, Pressable, StyleSheet, Text } from "react-native";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { central, semantic } from "../theme/tokens";

/** One quiet readiness cue; no repeating animation on the funeral-home kiosk. */
export function RegisterSignInButton({ disabled, loading, onPress }: { disabled: boolean; loading: boolean; onPress: () => void }) {
  const { reduceMotion } = useReducedMotion();
  const scale = useRef(new Animated.Value(1)).current;
  const [focused, setFocused] = useState(false);
  const ready = !disabled && !loading;
  useEffect(() => {
    scale.stopAnimation();
    scale.setValue(1);
    if (!ready || reduceMotion) return;
    const cue = Animated.sequence([
      Animated.timing(scale, { toValue: 1.018, duration: 280, easing: Easing.out(Easing.cubic), useNativeDriver: Platform.OS !== "web" }),
      Animated.timing(scale, { toValue: 1, duration: 420, easing: Easing.out(Easing.cubic), useNativeDriver: Platform.OS !== "web" }),
    ]);
    cue.start();
    return () => cue.stop();
  }, [ready, reduceMotion, scale]);
  return <Animated.View style={{ width: "100%", transform: [{ scale }] }}>
    <Pressable accessibilityRole="button" accessibilityLabel={loading ? "Sign In, loading" : "Sign In"} accessibilityState={{ disabled: !ready, busy: loading }} disabled={!ready} onPress={onPress}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} style={({ pressed }) => [styles.button, !ready && styles.disabled, pressed && ready && styles.pressed, focused && styles.focused]}>
      {loading ? <ActivityIndicator color={semantic.text.inverse} accessibilityLabel="Saving Your Entry" /> : <Text style={styles.label}>Sign In</Text>}
    </Pressable>
  </Animated.View>;
}
const styles = StyleSheet.create({
  button: { width: "100%", minHeight: 50, borderRadius: 12, paddingHorizontal: 20, paddingVertical: 12, backgroundColor: semantic.action.primary, justifyContent: "center", alignItems: "center", borderWidth: 2, borderColor: "transparent" },
  disabled: { opacity: 0.55 },
  pressed: { backgroundColor: semantic.action.primaryPressed },
  focused: { borderColor: central.action.primary },
  label: { color: semantic.text.inverse, fontFamily: central.typography.bodyMedium, fontSize: 16, lineHeight: 22 },
});
